import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import apiClient from '../api/client';

interface ApiKey {
  id: number;
  name: string;
  key?: string; // Only present when first created
  last_used_at: string | null;
  created_at: string;
}

interface ApiKeysProps {
  embedded?: boolean;
}

const ApiKeys: React.FC<ApiKeysProps> = ({ embedded = false }) => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load API keys
  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/api-keys');
      setKeys(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error loading API keys:', err);
      setError(err.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    try {
      const response = await apiClient.post('/api/v1/api-keys', {
        name: newKeyName,
      });

      // response.data contains the new key with the actual key value
      setNewlyCreatedKey(response.data.key);
      setNewKeyName('');
      setCreateDialogOpen(false);

      // Reload keys list
      await loadApiKeys();
      setError(null);
    } catch (err: any) {
      console.error('Error creating API key:', err);
      setError(err.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
  };

  const handleDeleteKey = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      try {
        await apiClient.delete(`/api/v1/api-keys/${id}`);
        await loadApiKeys();
        setError(null);
      } catch (err: any) {
        console.error('Error deleting API key:', err);
        setError(err.response?.data?.detail || 'Failed to delete API key');
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatLastUsed = (dateString: string | null) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: embedded ? '200px' : '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
              API Keys
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Manage your API keys for SDK authentication
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Key
          </Button>
        </Box>
      )}

      {embedded && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage your API keys for SDK authentication
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              backgroundColor: '#EF8997',
              '&:hover': { backgroundColor: '#e57383' },
            }}
          >
            Create Key
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {newlyCreatedKey && (
        <Alert
          severity="success"
          onClose={() => setNewlyCreatedKey(null)}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            API Key Created Successfully!
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              fontFamily: 'monospace',
            }}
          >
            <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
              {newlyCreatedKey}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopyKey(newlyCreatedKey)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            Save this key now - you won't be able to see it again!
          </Typography>
        </Alert>
      )}

      {!embedded && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Using API Keys:</strong> Set your API key as an environment variable{' '}
            <code>export CANARD_API_KEY="your-key"</code> or pass it to the SDK client:
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 1,
              p: 1,
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 1,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              overflow: 'auto',
            }}
          >
            {`from canard import Client\nclient = Client(api_key="your-key")`}
          </Box>
        </Alert>
      )}

      <TableContainer
        component={embedded ? 'div' : Paper}
        sx={{
          backgroundColor: embedded ? 'transparent' : '#0a0a0a',
          border: embedded ? 'none' : '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <Table size={embedded ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Key Prefix</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No API keys yet. Create one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      sk_***
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(key.created_at)}</TableCell>
                  <TableCell>{formatLastUsed(key.last_used_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteKey(key.id)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Key Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          },
        }}
      >
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Key Name"
            fullWidth
            variant="outlined"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="e.g., Production, Development, CI/CD"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            disabled={!newKeyName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) return content;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 4 }}>
      {content}
    </Box>
  );
};

export default ApiKeys;
